import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { GuestSession, TestResults } from '../types';
import { getGuestSessionExpiry, isGuestSessionValid } from '../utils/userTypeDetection';

/**
 * Hook for managing guest sessions
 */
export const useGuestSession = () => {
  const { 
    isGuestSession, 
    guestUser, 
    guestSession, 
    startGuestSession, 
    endGuestSession, 
    updateGuestSession 
  } = useAuth();

  const createGuestSession = useCallback((guestInfo: { 
    name: string; 
    age?: number; 
    department?: string; 
  }) => {
    return startGuestSession(guestInfo);
  }, [startGuestSession]);

  const terminateGuestSession = useCallback(() => {
    endGuestSession();
  }, [endGuestSession]);

  const saveGuestTestResults = useCallback((results: TestResults) => {
    if (guestSession) {
      updateGuestSession({
        ...guestSession,
        testResults: results
      });
    }
  }, [guestSession, updateGuestSession]);

  const isSessionValid = useCallback(() => {
    if (!guestSession) return false;
    return isGuestSessionValid(guestSession.expiresAt);
  }, [guestSession]);

  const getSessionTimeRemaining = useCallback(() => {
    if (!guestSession) return 0;
    const now = new Date().getTime();
    const expires = new Date(guestSession.expiresAt).getTime();
    return Math.max(0, expires - now);
  }, [guestSession]);

  const extendSession = useCallback(() => {
    if (guestSession) {
      const newExpiry = getGuestSessionExpiry();
      updateGuestSession({
        ...guestSession,
        expiresAt: newExpiry
      });
    }
  }, [guestSession, updateGuestSession]);

  return {
    // State
    isGuestSession,
    guestUser,
    guestSession,
    
    // Actions
    createGuestSession,
    terminateGuestSession,
    saveGuestTestResults,
    extendSession,
    
    // Utilities
    isSessionValid,
    getSessionTimeRemaining,
  };
};